// Ported from field-rep-frontend's label explorer, where it's a Vue composable:
// src/internal/views/label-explorer/useFrameCache.ts

import { useEffect, useRef, useState } from "react";
import { MISSING_IMAGE_PLACEHOLDER } from "@/config/constants";
import { TrackFrame } from "@/types/trackLabeling";
import { frameImageCache, resolveCachedImageUrl } from "@/utils/imageCache";
import {
  farthestFrame,
  nearestLoadedFrame,
  nextFrameToLoad,
} from "./framePriority";

/** Downloads at a time. */
const CONCURRENCY = 16;
/** About the viewer's height in CSS px: sharp enough while scrubbing, ~1.4 MB a frame in memory. */
const PREVIEW_HEIGHT = 800;
/** Previews kept: ~220 MB. */
const CAPACITY = 150;

// Frames download with fetch() when the bucket allows this origin (CORS), which decodes off the main
// thread. When a fetch fails but the same frame loads as an <img>, the bucket refused the origin:
// this turns off and frames load as images from then on.
let canFetchFrames = true;

export type FrameSize = { width: number; height: number };

export type FrameCache = {
  /** The first frame's full size, once one has decoded. */
  frameSize: FrameSize | null;
  previewAt: (index: number) => ImageBitmap | undefined;
  /** The index with a preview nearest `index`; null while none has loaded. */
  nearestPreviewIndex: (index: number) => number | null;
  /** Loaded or failed: waiting won't change what can be shown. */
  hasSettled: (index: number) => boolean;
  /**
   * A local link to the frame's downloaded file, for showing it in full without downloading it
   * again. Undefined when the frame loaded through an <img> (the bucket refused CORS), or isn't
   * cached.
   */
  fullFrameUrlAt: (index: number) => string | undefined;
};

type DecodedFrame = {
  preview: ImageBitmap;
  size: FrameSize;
  /** The downloaded file, when fetch() got it. null when the frame loaded through an <img>. */
  file: Blob | null;
};

/**
 * One track's downloads. A new one starts when the frames change; the old one is retired, so its
 * downloads that finish late see they're no longer wanted.
 */
type TrackDownloads = {
  /** The frames' image ids, to tell this track from another. */
  key: string;
  /** Updated when the task reloads with fresh signed links for the same frames. */
  frames: TrackFrame[];
  previews: Map<number, ImageBitmap>;
  /** Local links to the downloaded files, kept and dropped with the previews. */
  fileLinks: Map<number, string>;
  loading: Set<number>;
  /** Not retried, so a broken frame isn't requested over and over. */
  failed: Set<number>;
  fetches: Set<AbortController>;
  images: Set<HTMLImageElement>;
  frameSize: FrameSize | null;
  isRetired: boolean;
};

const framesKeyOf = (frames: TrackFrame[]) =>
  frames.map((frame) => frame.fileName).join("\n");

const startTrackDownloads = (frames: TrackFrame[]): TrackDownloads => {
  return {
    key: framesKeyOf(frames),
    frames,
    previews: new Map(),
    fileLinks: new Map(),
    loading: new Set(),
    // A frame without an image counts as failed, so it's never requested.
    failed: new Set(
      frames.flatMap((frame, index) =>
        frame.imageUrl === MISSING_IMAGE_PLACEHOLDER ? [index] : [],
      ),
    ),
    fetches: new Set(),
    images: new Set(),
    frameSize: null,
    isRetired: false,
  };
};

/** Closes the previews and cancels the downloads of a track no longer shown. */
const retireTrackDownloads = (downloads: TrackDownloads) => {
  downloads.isRetired = true;
  downloads.previews.forEach((preview) => preview.close());
  downloads.previews.clear();
  downloads.fileLinks.forEach((link) => URL.revokeObjectURL(link));
  downloads.fileLinks.clear();
  downloads.fetches.forEach((controller) => controller.abort());
  downloads.images.forEach((image) => {
    image.src = "";
  });
};

// The createImageBitmap options this cache uses (ImageBitmapOptions, spelled out for ESLint).
type PreviewOptions = {
  resizeWidth: number;
  resizeHeight: number;
  resizeQuality: "medium";
};

/** Shrinks to PREVIEW_HEIGHT, keeping the frame's own aspect ratio. */
const previewOptions = (size: FrameSize): PreviewOptions => {
  const scale = Math.min(1, PREVIEW_HEIGHT / size.height);
  return {
    resizeWidth: Math.round(size.width * scale),
    resizeHeight: Math.round(size.height * scale),
    resizeQuality: "medium",
  };
};

/** The frame's bytes. Null when fetch() fails without a response: no CORS, or no network. */
const fetchFrame = async (
  downloads: TrackDownloads,
  url: string,
): Promise<Blob | null> => {
  const controller = new AbortController();
  downloads.fetches.add(controller);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return await response.blob();
  } catch (error) {
    if (error instanceof TypeError) {
      return null;
    }
    throw error;
  } finally {
    downloads.fetches.delete(controller);
  }
};

const decodeBlob = async (
  downloads: TrackDownloads,
  blob: Blob,
): Promise<DecodedFrame> => {
  const knownSize = downloads.frameSize;
  if (knownSize) {
    // Height only: the browser keeps this frame's own aspect ratio, e.g. for a frame of another size.
    const { resizeHeight, resizeQuality } = previewOptions(knownSize);
    return {
      preview: await createImageBitmap(blob, { resizeHeight, resizeQuality }),
      size: knownSize,
      file: blob,
    };
  }

  // Decodes the first frame in full to learn its size, then shrinks that bitmap rather than
  // decoding the bytes a second time.
  const fullFrame = await createImageBitmap(blob);
  const size = { width: fullFrame.width, height: fullFrame.height };
  try {
    return {
      preview: await createImageBitmap(fullFrame, previewOptions(size)),
      size,
      file: blob,
    };
  } finally {
    fullFrame.close();
  }
};

/** The frame through an <img>. Null when its track is retired before it loads. */
const decodeImage = async (
  downloads: TrackDownloads,
  url: string,
): Promise<DecodedFrame | null> => {
  const image = new Image();
  downloads.images.add(image);
  try {
    // onload rather than decode(): decode() would hold each whole 4K frame decoded (~33 MB) along
    // with its preview, for all 16 downloads at once.
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = reject;
      image.src = url;
    });
    // Skips the shrinking for a retired track: shrinking an <img> blocks the main thread.
    if (downloads.isRetired) {
      return null;
    }

    const size = { width: image.naturalWidth, height: image.naturalHeight };
    return {
      preview: await createImageBitmap(image, previewOptions(size)),
      size,
      file: null,
    };
  } finally {
    downloads.images.delete(image);
  }
};

/** The frame's preview. Null when its track is retired before it decodes. */
const decodeFrame = async (
  downloads: TrackDownloads,
  url: string,
): Promise<DecodedFrame | null> => {
  if (!canFetchFrames) {
    return decodeImage(downloads, url);
  }

  const blob = await fetchFrame(downloads, url);
  if (blob) {
    return downloads.isRetired ? null : decodeBlob(downloads, blob);
  }

  const decoded = await decodeImage(downloads, url);
  // Stops using fetch() once an <img> loads where fetch() failed: the network is fine, so the bucket
  // refused this origin (CORS).
  canFetchFrames = false;
  return decoded;
};

/**
 * Downloads a track's frames, nearest the target first (framePriority), and keeps each as a small
 * decoded preview, so a scrubber step draws one in about a millisecond instead of decoding a 4K
 * frame. Re-renders its caller when a preview arrives. Starts over when the frames' image ids
 * change; retires the downloads on unmount.
 */
export const useFrameCache = (
  frames: TrackFrame[],
  targetIndex: number,
): FrameCache => {
  const [, setUpdateCount] = useState(0);
  const downloadsRef = useRef<TrackDownloads | null>(null);
  const framesRef = useRef(frames);
  framesRef.current = frames;
  const targetIndexRef = useRef(targetIndex);
  targetIndexRef.current = targetIndex;
  const framesKey = framesKeyOf(frames);

  // Re-renders the caller, e.g. when the preview it shows has arrived.
  const rerender = () => setUpdateCount((count) => count + 1);

  const evictBeyondCapacity = (downloads: TrackDownloads) => {
    while (downloads.previews.size > CAPACITY) {
      const frame = farthestFrame(
        targetIndexRef.current,
        downloads.previews.keys(),
      )!;
      downloads.previews.get(frame)!.close();
      downloads.previews.delete(frame);
      const fileLink = downloads.fileLinks.get(frame);
      if (fileLink) {
        URL.revokeObjectURL(fileLink);
        downloads.fileLinks.delete(frame);
      }
    }
  };

  const startDownloads = (downloads: TrackDownloads) => {
    // Downloads one frame at a time until the frame size is known (shrinking needs it), then up to
    // CONCURRENCY.
    const limit = downloads.frameSize ? CONCURRENCY : 1;
    while (downloads.loading.size < limit) {
      const frame = nextFrameToLoad({
        frameCount: downloads.frames.length,
        target: targetIndexRef.current,
        loaded: new Set(downloads.previews.keys()),
        loading: downloads.loading,
        failed: downloads.failed,
        capacity: CAPACITY,
      });
      if (frame === null) {
        return;
      }

      void downloadFrame(downloads, frame);
    }
  };

  const downloadFrame = async (downloads: TrackDownloads, frame: number) => {
    downloads.loading.add(frame);
    try {
      // Resolved just before the request, not when the track opened: a signed link stored hours
      // ago for the same image may have expired since.
      const { fileName, imageUrl } = downloads.frames[frame];
      const decoded = await decodeFrame(
        downloads,
        resolveCachedImageUrl(frameImageCache, fileName, imageUrl),
      );
      if (!decoded) {
        return;
      }

      // Closes the preview of a track retired while it was being made, e.g. the next track opened.
      if (downloads.isRetired) {
        decoded.preview.close();
        return;
      }

      downloads.frameSize ??= decoded.size;
      downloads.previews.set(frame, decoded.preview);
      if (decoded.file) {
        downloads.fileLinks.set(frame, URL.createObjectURL(decoded.file));
      }
      evictBeyondCapacity(downloads);
      rerender();
    } catch {
      if (!downloads.isRetired) {
        downloads.failed.add(frame);
        rerender();
      }
    } finally {
      if (!downloads.isRetired) {
        downloads.loading.delete(frame);
        startDownloads(downloads);
      }
    }
  };

  // Keeps the current track's frames up to date, e.g. their signed links after the task reloads.
  if (downloadsRef.current?.key === framesKey) {
    downloadsRef.current.frames = frames;
  }

  useEffect(() => {
    const downloads = startTrackDownloads(framesRef.current);
    downloadsRef.current = downloads;
    startDownloads(downloads);
    rerender();
    return () => retireTrackDownloads(downloads);
    // Restarts only when the frames change, not when their signed URLs are refreshed.
  }, [framesKey]);

  useEffect(() => {
    if (downloadsRef.current) {
      startDownloads(downloadsRef.current);
    }
  }, [targetIndex]);

  // Until the effect above has started the new track's downloads, nothing from the previous track
  // is shown with the new track's frames.
  const downloads =
    downloadsRef.current?.key === framesKey ? downloadsRef.current : null;

  return {
    frameSize: downloads?.frameSize ?? null,
    previewAt: (index) => downloads?.previews.get(index),
    nearestPreviewIndex: (index) =>
      downloads ? nearestLoadedFrame(index, downloads.previews.keys()) : null,
    hasSettled: (index) =>
      !!downloads &&
      (downloads.previews.has(index) || downloads.failed.has(index)),
    fullFrameUrlAt: (index) => downloads?.fileLinks.get(index),
  };
};
